// PaywallSheet.jsx
//
// Mounted once at the AppShell level. Subscribes to the subscription
// module's paywall queue. When any feature hits a 402 from the API or
// otherwise calls presentPaywall(), this opens a sheet showing three
// product tiles (monthly / annual / lifetime) and Restore Purchases.
//
// The pricing strings are read from the live RevenueCat offering when
// available — that's the only place Apple-localized prices land. If the
// SDK isn't initialized (web, missing env), we fall back to hard-coded
// USD strings so the layout still renders.

import { useEffect, useState } from 'react';
import {
  usePaywall,
  dismissPaywall,
} from '../lib/subscription.js';
import {
  getCurrentOffering,
  purchasePackage,
  restorePurchases,
  isPurchasesAvailable,
} from '../lib/purchases.js';
import { startCheckout, isStripeAvailable } from '../lib/stripe.js';
import { isIOS } from '../lib/platform.js';

// Fallback prices. Used on web and during a brief flash before the
// offering loads on first open. Match the values configured in App
// Store Connect.
const FALLBACK = {
  monthly:  { price: '$7.99',   period: '/month',  trial: '7-day free trial', tag: null },
  annual:   { price: '$59.99',  period: '/year',   trial: 'Save 37%',          tag: 'BEST VALUE' },
  lifetime: { price: '$149',    period: 'one time', trial: 'Pay once. Yours forever.', tag: null },
};

// Map our internal package keys to RC's identifier conventions. RC
// auto-creates standard identifiers ($rc_monthly / $rc_annual / $rc_lifetime)
// when you mark a package with one of its built-in durations; the bare
// `monthly` / `annual` / `lifetime` aliases are the fallback if someone
// configures a custom package identifier matching our key name.
const PACKAGE_KEYS = {
  monthly:  ['$rc_monthly',  'monthly'],
  annual:   ['$rc_annual',   'annual'],
  lifetime: ['$rc_lifetime', 'lifetime'],
};

function pickPackage(offering, key) {
  if (!offering) return null;
  const aliases = PACKAGE_KEYS[key] || [];
  const all = offering.availablePackages || [];
  for (const id of aliases) {
    const found = all.find(p => p.identifier === id);
    if (found) return found;
  }
  // Fallback: try matching by product type.
  return null;
}

function reasonHeadline(reason) {
  switch (reason) {
    case 'brief_limit': return "You've used all your free briefs";
    case 'tts_limit':   return 'Unlock unlimited narration';
    case 'stt_limit':   return 'Unlock unlimited voice input';
    case 'llm_limit':   return 'Unlock unlimited AI features';
    case 'theme':       return 'Premium themes are a Pro perk';
    case 'voice':       return 'Premium voices are a Pro perk';
    default:            return 'Unlock everything in Consistency';
  }
}

function reasonSub(reason) {
  switch (reason) {
    case 'brief_limit': return 'Free includes 3 Intel Briefs per week. Pro removes the cap.';
    case 'tts_limit':   return 'Free includes 10 narrations per day. Pro removes the cap.';
    case 'stt_limit':   return 'Free includes 5 voice transcriptions per day. Pro removes the cap.';
    case 'llm_limit':   return 'Free includes 20 AI requests per day. Pro removes the cap.';
    default:            return 'On-demand briefs, every voice, every theme, no caps.';
  }
}

export function PaywallSheet() {
  const pending = usePaywall();
  const [offering, setOffering] = useState(null);
  const [busy, setBusy] = useState(null);          // 'monthly' | 'annual' | 'lifetime' | 'restore' | null
  const [error, setError] = useState(null);

  // Load the offering whenever the sheet opens.
  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    (async () => {
      const off = await getCurrentOffering();
      if (!cancelled) setOffering(off);
    })();
    return () => { cancelled = true; };
  }, [pending]);

  if (!pending) return null;

  const monthly  = pickPackage(offering, 'monthly');
  const annual   = pickPackage(offering, 'annual');
  const lifetime = pickPackage(offering, 'lifetime');

  const tiles = [
    {
      key: 'annual',
      pkg: annual,
      title: 'Annual',
      ...FALLBACK.annual,
      price: annual?.product?.priceString || FALLBACK.annual.price,
      featured: true,
    },
    {
      key: 'monthly',
      pkg: monthly,
      title: 'Monthly',
      ...FALLBACK.monthly,
      price: monthly?.product?.priceString || FALLBACK.monthly.price,
    },
    {
      key: 'lifetime',
      pkg: lifetime,
      title: 'Lifetime',
      ...FALLBACK.lifetime,
      price: lifetime?.product?.priceString || FALLBACK.lifetime.price,
    },
  ];

  const onClose = () => {
    if (busy) return;
    dismissPaywall();
  };

  const onBuy = async (tile) => {
    setError(null);

    // Platform split. iOS goes through RevenueCat → StoreKit because
    // Apple's terms require IAP for digital subscriptions inside the iOS
    // shell. Desktop and web go through Stripe Checkout — same plans,
    // same prices, just hosted on Stripe instead of Apple.
    if (isIOS()) {
      if (!isPurchasesAvailable()) {
        setError('Subscriptions are temporarily unavailable. Try again shortly.');
        return;
      }
      if (!tile.pkg) {
        setError('This plan is unavailable right now. Try again shortly.');
        return;
      }
      setBusy(tile.key);
      const result = await purchasePackage(tile.pkg);
      setBusy(null);
      if (result.ok) {
        dismissPaywall();
      } else if (result.userCancelled) {
        // Silent — user just dismissed StoreKit.
      } else {
        setError(result.error?.message || 'Purchase failed. Try again.');
      }
      return;
    }

    // Stripe path (desktop + web).
    if (!isStripeAvailable()) {
      setError('Subscriptions are not available in this build.');
      return;
    }
    setBusy(tile.key);
    const result = await startCheckout(tile.key);
    // We don't dismiss the paywall here — the user has been redirected (web)
    // or sent to their default browser (desktop). When they return after
    // payment, the success page polls subscription state and the paywall
    // gets dismissed on the next refresh. setBusy(null) is mainly so the
    // sheet doesn't look stuck if Stripe Checkout fails to open at all.
    setBusy(null);
    if (!result.ok) {
      if (result.userNotSignedIn) {
        setError('Sign in to subscribe — guests can browse, but purchases need an account.');
      } else {
        setError(result.error?.message || 'Could not start checkout. Try again.');
      }
    }
  };

  const onRestore = async () => {
    setError(null);

    // iOS path — explicit Restore Purchases is a StoreKit / App Review
    // requirement.
    if (isIOS()) {
      if (!isPurchasesAvailable()) {
        setError('Restore is temporarily unavailable. Try again shortly.');
        return;
      }
      setBusy('restore');
      const result = await restorePurchases();
      setBusy(null);
      if (result.ok) {
        dismissPaywall();
      } else {
        setError(result.error?.message || 'Nothing to restore.');
      }
      return;
    }

    // Desktop / web: there's no "restore" concept on Stripe — the user's
    // subscription is bound to their Stripe customer, which is bound to
    // their Supabase account. As long as they're signed in, the tier shows
    // up. Tell them so they don't think the button is broken, and offer to
    // re-pull entitlements just in case.
    setError("Already paid? Your subscription is linked to this account — sign in on any device with the same email and it'll appear automatically.");
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          zIndex: 230, animation: 'fadeIn 140ms ease-out',
        }}
      />
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 231,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          padding: 0,
          pointerEvents: 'none',
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="paywall-title"
          style={{
            width: '100%',
            maxWidth: 520,
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'rgba(20,24,32,0.98)',
            border: '1px solid var(--stroke-1)',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            padding: '22px 22px calc(22px + env(safe-area-inset-bottom))',
            boxShadow: '0 -24px 80px rgba(0,0,0,0.7)',
            animation: 'sheetUp 260ms cubic-bezier(0.22,1,0.36,1)',
            pointerEvents: 'auto',
          }}
        >
          {/* Drag handle */}
          <div
            onClick={onClose}
            style={{
              width: 40, height: 4, borderRadius: 4,
              background: 'rgba(255,255,255,0.18)',
              margin: '0 auto 14px',
            }}
          />

          <div
            id="paywall-title"
            style={{
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22,
              color: 'var(--fg-1)', textAlign: 'center', marginBottom: 6,
            }}
          >
            {reasonHeadline(pending.reason)}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--fg-3)',
              textAlign: 'center', lineHeight: 1.5, marginBottom: 18,
              padding: '0 4px',
            }}
          >
            {reasonSub(pending.reason)}
          </div>

          {/* Benefits list */}
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--stroke-1)',
              borderRadius: 14,
              padding: '12px 14px',
              marginBottom: 18,
              fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-2)',
              lineHeight: 1.7,
            }}
          >
            <Row>Unlimited Intel Briefs, on demand</Row>
            <Row>Every premium voice + narration on tap</Row>
            <Row>Voice agenda + recap, no daily cap</Row>
            <Row>Premium themes (Executive, Girlboss)</Row>
            <Row>Early access to new features</Row>
          </div>

          {/* Tiles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {tiles.map(t => (
              <Tile key={t.key} tile={t} busy={busy} onBuy={onBuy} />
            ))}
          </div>

          {error && (
            <div
              role="alert"
              style={{
                color: '#ff8c8c', fontFamily: 'var(--font-body)', fontSize: 12.5,
                textAlign: 'center', marginBottom: 10,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 6, marginBottom: 8 }}>
            <button
              onClick={onRestore}
              disabled={!!busy}
              style={linkBtn}
            >
              {busy === 'restore' ? 'Restoring…' : 'Restore purchases'}
            </button>
            <span style={{ color: 'var(--fg-4, rgba(255,255,255,0.25))' }}>•</span>
            <button onClick={onClose} disabled={!!busy} style={linkBtn}>
              Not now
            </button>
          </div>

          <div
            style={{
              fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-4, rgba(255,255,255,0.45))',
              textAlign: 'center', lineHeight: 1.5, marginTop: 8,
            }}
          >
            Subscriptions auto-renew until cancelled. Manage or cancel anytime in Settings → Apple ID → Subscriptions.
            <br />
            <a href="https://www.proxera.io/terms"   style={footLink} target="_blank" rel="noopener noreferrer">Terms</a>
            {' · '}
            <a href="https://www.proxera.io/privacy" style={footLink} target="_blank" rel="noopener noreferrer">Privacy</a>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ color: 'var(--accent-bright, #47A6FF)', fontWeight: 700 }}>+</span>
      <span>{children}</span>
    </div>
  );
}

function Tile({ tile, busy, onBuy }) {
  const isBusy = busy === tile.key;
  const disabled = !!busy;
  // On iOS the purchase flow goes through StoreKit (RC), so "Confirming
  // with Apple…" is accurate. On web/desktop it goes to Stripe Checkout
  // and the prior copy was misleading — the user actually waits on Stripe,
  // not Apple. isStripeAvailable() is the canonical "we're on a Stripe
  // platform" check used by the rest of this file.
  const isStripePath = isStripeAvailable();
  const confirmingCopy = isStripePath ? 'Opening Stripe Checkout…' : 'Confirming with Apple…';
  return (
    <button
      onClick={() => onBuy(tile)}
      disabled={disabled}
      style={{
        position: 'relative',
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        borderRadius: 14,
        background: tile.featured
          ? 'linear-gradient(180deg, rgba(71,166,255,0.16), rgba(45,125,224,0.10))'
          : 'rgba(255,255,255,0.04)',
        border: tile.featured
          ? '1px solid rgba(71,166,255,0.55)'
          : '1px solid var(--stroke-1)',
        color: 'var(--fg-1)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !isBusy ? 0.6 : 1,
        boxShadow: tile.featured ? '0 8px 24px rgba(45,125,224,0.25)' : 'none',
        fontFamily: 'inherit',
      }}
    >
      {tile.tag && (
        <span
          style={{
            position: 'absolute', top: -10, right: 14,
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 10,
            letterSpacing: 0.6,
            background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
            color: '#fff', padding: '4px 10px', borderRadius: 999,
            boxShadow: '0 4px 12px rgba(45,125,224,0.4)',
          }}
        >
          {tile.tag}
        </span>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15.5 }}>
          {tile.title}
        </span>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15.5 }}>
          {tile.price}
          <span style={{ fontWeight: 500, color: 'var(--fg-3)', fontSize: 12.5, marginLeft: 4 }}>
            {tile.period}
          </span>
        </span>
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
        {isBusy ? confirmingCopy : tile.trial}
      </div>
    </button>
  );
}

const linkBtn = {
  background: 'transparent',
  border: 'none',
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-body)',
  fontSize: 12.5,
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};

const footLink = {
  color: 'var(--fg-3)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
